'use client';

import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useAppSelector } from '@/store/hooks';
import { getDrawerContent } from '@/store/reducers/drawer-content-slice';
import { MethodResponse } from '@alien-platform/bo-sdk-v2';
import { Divider, Stack, Tab, Tabs } from '@mui/material';

import { Game, GameDetailsTab } from '@/types/general';
import Loader from '@/components/core/loader';

import { GameDetails } from './tabs/game-details-tab';
import { GameJurisdictions } from './tabs/game-jurisdictions-tab';
import { GameSettings } from './tabs/game-settings-tab';

export default function GameDetailsContent(): React.JSX.Element | null {
  const [tabValue, setTabValue] = useState(GameDetailsTab.DETAILS);
  const searchParams = useSearchParams();
  const opId = searchParams.get('opId');
  const [game, setGame] = useState<Game>();
  const [isLoading, setIsLoading] = useState(false);

  const { GENERAL_GAME_DETAILS: gameData } = useAppSelector(getDrawerContent);

  const fetchData = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const res = await fetch(`/api/games?opId=${opId}&gameId=${gameData?.gameId}`);
      if (res.ok) {
        const result: MethodResponse<Game> = await res.json();
        if (result.success) {
          setGame(result?.data);
        }
      }
    } catch (e) {
      //Noob
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void fetchData();
  }, []);

  const tabList = [
    {
      id: GameDetailsTab.DETAILS,
      label: 'UI Config',
      content: <GameDetails game={game} />,
    },
    {
      id: GameDetailsTab.SETTINGS,
      label: 'Settings',
      content: <GameSettings game={game} />,
    },
    {
      id: GameDetailsTab.JURISDICTIONS,
      label: 'Jurisdictions',
      content: <GameJurisdictions game={game} />,
    },
    {
      id: GameDetailsTab.AVAILABLE_OPERATORS,
      label: 'Available Operators',
      content: <>Available Operators</>,
    },
    {
      id: GameDetailsTab.CURRENCIES,
      label: 'Currencies',
      content: <>Currencies</>,
    },
    {
      id: GameDetailsTab.ACTIVITY_LOGS,
      label: 'Activity Logs',
      content: <>Activity Logs</>,
    },
  ];

  const handleChange = (event: React.SyntheticEvent, newValue: GameDetailsTab): void => {
    setTabValue(newValue);
  };

  return (
    <>
      <Tabs value={tabValue} onChange={handleChange}>
        {tabList.map((item) => (
          <Tab key={item.id} label={item.label} value={item.id} />
        ))}
      </Tabs>
      <Divider />
      {isLoading ? (
        <Loader />
      ) : (
        tabList.map((item) => (
          <Stack sx={{ pt: 2, display: item.id === tabValue ? 'flex' : 'none' }} key={item.id}>
            {item.content}
          </Stack>
        ))
      )}
    </>
  );
}
